// src/authContetxt.js
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from './utils/axiosConfig';

export const AuthContext = createContext({
  authToken: null,
  isAuthenticated: false,
  setAuthToken: () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize from localStorage to persist across refreshes (current flow)
  const [authToken, setAuthToken] = useState(() => {
    try {
      return localStorage.getItem('authToken');
    } catch {
      return null;
    }
  });

  // Keep localStorage in sync with state
  useEffect(() => {
    try {
      if (authToken) {
        localStorage.setItem('authToken', authToken);
      } else {
        localStorage.removeItem('authToken');
      }
    } catch {
      /* ignore storage errors */
    }
  }, [authToken]);

  const isAuthenticated = !!authToken;

  // Centralized logout:
  // 1) Tell backend to revoke the session (bump sessionVersion)
  // 2) Clear local token and redirect to login
 const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch {
      /* ignore */
    } finally {
      try {
        localStorage.removeItem('authToken');
      } catch {
        /* ignore */
      }
      setAuthToken(null);

      const next =
        location && location.pathname
          ? `${location.pathname}${location.search || ''}`
          : '/';
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [location, navigate]); // deps

  const value = useMemo(
    () => ({
      authToken,
      isAuthenticated,
      setAuthToken,
      logout,
    }),
    [authToken, isAuthenticated, logout] // now logout is included
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


// Optional convenience hook
export const useAuth = () => useContext(AuthContext);
